import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Badge,
  Button,
  PermissionsProvider,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import type {AdminBusinessAffiliateTier} from '@/types';
import {BusinessAffiliateRewardModel} from '@/types';
import {capitalizeWords} from '@/utils/dashboard';

const CELL = 'pe-4';

const cap = (value?: number): string =>
  value === undefined || value === -1 ? 'No cap' : `$${value.toLocaleString()}`;

const describeReward = (tier: AdminBusinessAffiliateTier): string => {
  switch (tier.rewardModel) {
    case BusinessAffiliateRewardModel.FeeShare:
      return `${tier.referrerSharePercent ?? 0}% of the fee`;
    case BusinessAffiliateRewardModel.FlatAtThreshold:
      return `$${(tier.flatRewardUSD ?? 0).toLocaleString()} at $${(
        tier.volumeThresholdUSD ?? 0
      ).toLocaleString()} sent`;
    case BusinessAffiliateRewardModel.VolumePercentage:
      return `${tier.volumeRewardBasisPoints ?? 0} bps of volume`;
  }
};

const AffiliateTierTable = ({
  items,
  isSaving,
  onEdit,
  onMakeDefault,
}: {
  items: AdminBusinessAffiliateTier[];
  isSaving: boolean;
  onEdit: (tier: AdminBusinessAffiliateTier) => void;
  onMakeDefault: (tier: AdminBusinessAffiliateTier) => void;
}) => (
  <Table className="overflow-hidden">
    <Thead>
      <Tr enableHover={false}>
        <Th className={`ps-5 ${CELL}`}>Name</Th>
        <Th className={CELL}>Reward model</Th>
        <Th className={CELL}>Reward</Th>
        <Th className={CELL}>Referee discount</Th>
        <Th className={CELL}>Windows (activate/earn)</Th>
        <Th className={CELL}>Caps (referral/payout)</Th>
        <Th className={CELL}>Terms</Th>
        <Th className={CELL}>Status</Th>
        <Th>&nbsp;</Th>
      </Tr>
    </Thead>
    <TBody>
      {items.map((tier, index) => (
        <Tr
          key={tier.id}
          className={index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow'}
        >
          <Td className={`font-medium ps-5 ${CELL}`}>{tier.name}</Td>
          <Td className={CELL}>
            {capitalizeWords(tier.rewardModel.replace(/_/g, ' '))}
          </Td>
          <Td className={CELL}>{describeReward(tier)}</Td>
          <Td className={CELL}>{tier.refereeDiscountPercent ?? 0}%</Td>
          <Td className={CELL}>
            {tier.activationWindowDays}d / {tier.rewardWindowDays}d
          </Td>
          <Td className={CELL}>
            {cap(tier.maxRewardPerReferralUSD)} /{' '}
            {cap(tier.maxRewardPerPayoutUSD)}
          </Td>
          <Td className={CELL}>{tier.termsVersion}</Td>
          <Td className={CELL}>
            <div className="flex items-center gap-1">
              {tier.isDefault ? (
                <Badge label="Default" colorScheme="successful" />
              ) : null}
              {tier.isDeactivated ? (
                <Badge label="Deactivated" colorScheme="failed" />
              ) : null}
            </div>
          </Td>
          <Td className={CELL}>
            <PermissionsProvider
              permission={AfriexAdminPermission.BusinessAffiliateManage}
            >
              <div className="flex items-center justify-end gap-2">
                {/* A deactivated tier cannot become the default: it would leave
                    new referrals captured under terms nothing honours. */}
                {!tier.isDefault && !tier.isDeactivated ? (
                  <Button
                    variant="outline"
                    colorScheme="cyan"
                    disabled={isSaving}
                    onClick={() => onMakeDefault(tier)}
                    data-testid={`affiliate-tier-make-default-${tier.id}`}
                  >
                    Make default
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  colorScheme="cyan"
                  onClick={() => onEdit(tier)}
                  data-testid={`affiliate-tier-edit-${tier.id}`}
                >
                  Edit
                </Button>
              </div>
            </PermissionsProvider>
          </Td>
        </Tr>
      ))}
    </TBody>
  </Table>
);

export default AffiliateTierTable;
